import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { Observable, retry, timer } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);

  getTranslation(langPath: string): Observable<Translation> {
    console.log('[TranslocoLoader] Requested path:', langPath);

    // For scoped translations, Transloco sends the path as "scope/lang"
    // We need to load from ./assets/i18n/scope/lang.json

    if (langPath.includes('/')) {
      // Handle potential duplicate scope in path
      // Sometimes Transloco might send "scope/scope/lang" due to misconfiguration
      const parts = langPath.split('/');

      // If we have 3 parts and first two are identical, it's a duplicate
      if (parts.length === 3 && parts[0] === parts[1]) {
        // Fix the duplicate: "mzic-balance/mzic-balance/en" -> "mzic-balance/en"
        const fixedPath = `./assets/i18n/${parts[1]}/${parts[2]}.json`;
        console.log('[TranslocoLoader] Fixed duplicate path to:', fixedPath);
        return this.http.get<Translation>(fixedPath).pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => {
              console.log(`[TranslocoLoader] Retry ${retryCount} for ${fixedPath}:`, error);
              return timer(200 * retryCount); // 200ms, 400ms, 600ms delay
            }
          })
        );
      }

      // Normal scoped path: "scope/lang"
      if (parts.length >= 2) {
        // Just append .json to the path
        const fullPath = `./assets/i18n/${langPath}.json`;
        console.log(
          '[TranslocoLoader] Loading scoped translation from:',
          fullPath,
        );
        return this.http.get<Translation>(fullPath).pipe(
          retry({
            count: 3,
            delay: (error, retryCount) => {
              console.log(`[TranslocoLoader] Retry ${retryCount} for ${fullPath}:`, error);
              return timer(200 * retryCount); // 200ms, 400ms, 600ms delay
            }
          })
        );
      }
    }

    // Regular translation without scope
    const regularPath = `./assets/i18n/${langPath}.json`;
    console.log(
      '[TranslocoLoader] Loading regular translation from:',
      regularPath,
    );
    return this.http.get<Translation>(regularPath).pipe(
      retry({
        count: 3,
        delay: (error, retryCount) => {
          console.log(`[TranslocoLoader] Retry ${retryCount} for ${regularPath}:`, error);
          return timer(200 * retryCount); // 200ms, 400ms, 600ms delay
        }
      })
    );
  }
}
